import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import type { UserContext } from '@atlas/shared';
import { api, setCsrfToken } from '@/services/api';
import { setPreviewRole } from '@/lib/previewRole';

export type LoginResult =
  | { kind: 'authenticated' }
  | { kind: 'twoFactorRequired' }
  | { kind: 'mustEnrollTwoFactor' };

interface AuthContextType {
  user: UserContext | null;
  login: (email: string, password: string) => Promise<LoginResult>;
  loginDev: () => Promise<void>;
  logout: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  isAuthenticated: boolean;
  isLoading: boolean;
  mustChangePassword: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const DEV_MODE_KEY = 'atlas_dev_mode';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserContext | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  // Initialize session on mount: fetch CSRF token then restore user from cookie
  useEffect(() => {
    const devMode = import.meta.env.DEV && localStorage.getItem(DEV_MODE_KEY) === 'true';

    if (devMode) {
      setUser({
        userId: 'dev-user-001',
        email: 'dev@example.com',
        name: 'Development User',
        roles: ['admin', 'engineer'],
        divisions: ['org'],
      });
      setIsLoading(false);
      return;
    }

    async function initSession() {
      try {
        // Fetch CSRF token (always available, no auth required)
        const { csrfToken } = await api.auth.getCsrfToken();
        setCsrfToken(csrfToken);

        // Restore session from httpOnly cookie
        const { user: currentUser } = await api.auth.me();
        setUser(currentUser);
      } catch {
        // 401 = no valid session; clear state and proceed to login
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    }

    initSession();
  }, []);

  const login = async (email: string, password: string): Promise<LoginResult> => {
    const data = await api.auth.login({ email, password });

    if (data.twoFactorRequired) {
      return { kind: 'twoFactorRequired' };
    }

    if (data.mustEnrollTwoFactor) {
      return { kind: 'mustEnrollTwoFactor' };
    }

    // Refresh CSRF token now that the session cookie is set
    try {
      const { csrfToken } = await api.auth.getCsrfToken();
      setCsrfToken(csrfToken);
    } catch {
      // Non-fatal
    }

    localStorage.removeItem(DEV_MODE_KEY);
    setUser({
      userId: data.user.userId,
      email: data.user.email,
      name: data.user.name,
      roles: data.user.roles,
      divisions: data.user.divisions,
    });
    setMustChangePassword(data.mustChangePassword || false);

    return { kind: 'authenticated' };
  };

  const loginDev = async () => {
    // Dev login is only available in Vite dev builds — stripped from production bundles
    if (!import.meta.env.DEV) {
      throw new Error('Dev login is not available in production');
    }
    setUser({
      userId: 'dev-user-001',
      email: 'dev@example.com',
      name: 'Development User',
      roles: ['admin', 'engineer'],
      divisions: ['org'],
    });
    setMustChangePassword(false);
    localStorage.setItem(DEV_MODE_KEY, 'true');
  };

  // When a session expires, every in-flight request 401s at once and each one
  // dispatches `auth:unauthorized`. Run one logout; concurrent callers return.
  const logoutInFlight = useRef(false);
  const logout = async () => {
    if (logoutInFlight.current) return;
    logoutInFlight.current = true;
    try {
      try {
        const { unsubscribePush } = await import('@/services/push');
        await unsubscribePush();
      } catch {
        // best-effort — never block logout
      }
      try {
        await api.auth.logout();
      } catch {
        // Best-effort: clear local state regardless
      }
      setCsrfToken('');
      setUser(null);
      setMustChangePassword(false);
      localStorage.removeItem(DEV_MODE_KEY);
      // Otherwise the next person to sign in on this tab inherits the banner.
      setPreviewRole(null);
    } finally {
      logoutInFlight.current = false;
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    const { user: userData } = await api.auth.changePassword({ currentPassword, newPassword });

    // change-password issues a fresh access-token cookie; the CSRF token is
    // bound to it, so refresh the CSRF token to match the new session.
    try {
      const { csrfToken } = await api.auth.getCsrfToken();
      setCsrfToken(csrfToken);
    } catch {
      // Non-fatal
    }

    setUser({
      userId: userData.userId,
      email: userData.email,
      name: userData.name,
      roles: userData.roles,
      divisions: userData.divisions,
    });
    setMustChangePassword(false);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        loginDev,
        logout,
        changePassword,
        isAuthenticated: !!user,
        isLoading,
        mustChangePassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function useRequireAuth() {
  const { isAuthenticated, isLoading } = useAuth();
  return { isAuthenticated, isLoading };
}
